import { useState } from "react";

import type { PersonalButtonSet } from "../../api/client";
import type { EditableButtonRow } from "../composer/button-layout-model";
import { ButtonRowsPreview } from "./ButtonSetEditor";

export const copyButtonSetRows = (
  rows: readonly EditableButtonRow[]
): EditableButtonRow[] => rows.map((row) => ({
  id: `row-${crypto.randomUUID()}`,
  buttons: row.buttons.map((button) => ({
    ...button,
    id: `button-${crypto.randomUUID()}`
  }))
}));

const hasButtons = (rows: readonly EditableButtonRow[]) =>
  rows.some((row) => row.buttons.length > 0);

export function ButtonSetPicker({
  sets,
  currentRows,
  onLink,
  onCopy,
  onClose
}: {
  sets: PersonalButtonSet[];
  currentRows: EditableButtonRow[];
  onLink: (set: PersonalButtonSet) => void;
  onCopy: (rows: EditableButtonRow[]) => void;
  onClose: () => void;
}) {
  const [replacement, setReplacement] = useState<{
    item: PersonalButtonSet;
    mode: "link" | "copy";
  } | null>(null);

  const apply = (item: PersonalButtonSet, mode: "link" | "copy") => {
    if (hasButtons(currentRows)) {
      setReplacement({ item, mode });
      return;
    }
    if (mode === "link") onLink(item);
    else onCopy(copyButtonSetRows(item.buttonRows));
  };

  const confirmReplacement = () => {
    if (!replacement) return;
    if (replacement.mode === "link") onLink(replacement.item);
    else onCopy(copyButtonSetRows(replacement.item.buttonRows));
  };

  return <div className="button-set-picker-backdrop" role="dialog" aria-modal="true" aria-label="Выбор личного набора кнопок">
    <section className="button-set-picker">
      <header>
        <div><p className="eyebrow">Личная библиотека</p><h2>Выберите набор</h2></div>
        <button type="button" aria-label="Закрыть выбор набора" onClick={onClose}>×</button>
      </header>
      {sets.length === 0 ? <div className="empty-state"><strong>Личных наборов пока нет</strong><p>Создайте набор в разделе «Ещё».</p></div> : <div className="button-set-picker-list">{sets.map((item) => <article key={item.id}>
        <div><strong>{item.name}</strong><small>{item.buttonRows.reduce((total, row) => total + row.buttons.length, 0)} кнопок</small></div>
        <ButtonRowsPreview rows={item.buttonRows} />
        <div className="button-set-picker-actions">
          <button className="primary-button" type="button" aria-label={`Связать набор ${item.name}`} onClick={() => apply(item, "link")}>Связать</button>
          <button type="button" aria-label={`Скопировать набор ${item.name}`} onClick={() => apply(item, "copy")}>Скопировать</button>
        </div>
      </article>)}</div>}
      {replacement && <section className="button-set-replace-confirm" aria-label="Подтверждение замены кнопок">
        <strong>Заменить текущие кнопки набором «{replacement.item.name}»?</strong>
        <p>{replacement.mode === "link" ? "Пост будет связан с набором: изменения набора будут применяться к этому посту." : "Будет создана независимая копия, которую можно менять только в этом посте."}</p>
        <div><button type="button" onClick={() => setReplacement(null)}>Оставить текущие</button><button className="primary-button" type="button" onClick={confirmReplacement}>{replacement.mode === "link" ? "Заменить и связать" : "Заменить копией"}</button></div>
      </section>}
    </section>
  </div>;
}
